/**
 * TEK TEMA KAYNAĞI — koyu / açık.
 *
 * Sorun: tema düğmesi yalnız Home ekranının kendi kabuğunu (cp-light) boyuyordu;
 * SİDEBAR ve tüm paneller koyu kalıyordu (kullanıcı bildirdi: "sidebar dahil
 * her yer değişsin"). Renkler her dosyada elle yazıldığı için tek tek çevirmek
 * yerine palet CSS DEĞİŞKENİ hâline getirildi: panel teması, sidebar ve uygulama
 * kabuğu artık var(--cp-*) kullanıyor, burada tanımlanan iki palet ikisini de
 * çeviriyor.
 *
 * Kullanım:
 *   import { applyTheme, getTheme, setTheme, onThemeChange } from "util/theme";
 */

const KEY = "cp_theme";
const EVENT = "cp-theme-change";

export const THEMES = ["dark", "light"];

/* Palet — panel/sidebar/kabuk hepsi bu değişkenleri kullanır.
   Açık palette metin/zemin kontrastı WCAG AA'yı geçecek şekilde seçildi
   (gövde metni #1f2937 üzerine #ffffff ≈ 13:1, ikincil #5a6478 ≈ 6:1). */
const PALETTE = {
  dark: {
    "--cp-bg": "#06060f",
    "--cp-bg2": "#0c1222",
    "--cp-surface": "#111827",
    "--cp-surface2": "#0d1424",
    "--cp-elevated": "#0f0f1e",
    "--cp-border": "#1e293b",
    "--cp-border-soft": "rgba(148,163,184,.18)",
    "--cp-title": "#f1f5f9",
    "--cp-text": "#e2e8f0",
    "--cp-text2": "#cbd5e1",
    "--cp-muted": "#94a3b8",
    "--cp-faint": "#64748b",
    /* Vurgu tonları koyu zemine göre PASTEL seçilmişti; açık temada okunmuyor
       (canlı: "Rapor İndir" #c4b5fd beyaz üzerinde ~1.9:1). Açıkta koyu
       karşılıkları kullanılıyor — hepsi AA'yı geçiyor. */
    "--cp-violet": "#c4b5fd",
    /* DOLGU icin: --cp-violet bir METIN rengidir ve temaya gore
       terslenir; dugme zemini yapilinca koyu temada acik lavanta
       uzerine beyaz yazi biniyor ve okunmuyor. Bu simge iki temada
       da ayni kaliyor, uzerine beyaz metin her zaman okunur. */
    "--cp-violet-fill": "#7c3aed",
    "--cp-ok": "#4ade80",
    "--cp-warn": "#fbbf24",
    "--cp-danger": "#f87171",
    "--cp-info": "#60a5fa",
    "--cp-side-bg": "linear-gradient(180deg,#0c1222 0%,#0f172a 40%,#0c1222 100%)",
    "--cp-side-item": "rgba(255,255,255,.04)",
    "--cp-side-item-hover": "rgba(255,255,255,.09)",
    "--cp-side-active": "linear-gradient(135deg,rgba(99,102,241,.18),rgba(139,92,246,.12))",
    "--cp-shadow": "0 10px 30px rgba(0,0,0,.35)",
    "--cp-field": "rgba(0,0,0,.3)",
    "--cp-scheme": "dark",
  },
  light: {
    "--cp-bg": "#f5f6fa",
    "--cp-bg2": "#eef1f7",
    "--cp-surface": "#ffffff",
    "--cp-surface2": "#f8fafc",
    "--cp-elevated": "#ffffff",
    "--cp-border": "#e2e8f0",
    "--cp-border-soft": "rgba(15,23,42,.12)",
    "--cp-title": "#0f172a",
    "--cp-text": "#1f2937",
    "--cp-text2": "#334155",
    "--cp-muted": "#5a6478",
    "--cp-faint": "#5f6b7f",
    "--cp-violet": "#6d28d9",
    "--cp-violet-fill": "#7c3aed",
    "--cp-ok": "#15803d",
    "--cp-warn": "#b45309",
    "--cp-danger": "#dc2626",
    "--cp-info": "#1d4ed8",
    "--cp-side-bg": "linear-gradient(180deg,#ffffff 0%,#f7f8fc 100%)",
    "--cp-side-item": "rgba(15,23,42,.04)",
    "--cp-side-item-hover": "rgba(15,23,42,.07)",
    "--cp-side-active": "linear-gradient(135deg,rgba(99,102,241,.14),rgba(139,92,246,.10))",
    "--cp-shadow": "0 10px 30px rgba(15,23,42,.10)",
    "--cp-field": "#ffffff",
    "--cp-scheme": "light",
  },
};

/* Açık temada koyu varsayılanları olan yerler (antd, eski satır içi stiller,
   ManagerCenter kartları) için son rötuş katmanı. */
const GLOBAL_CSS = `
:root[data-theme] body{background:var(--cp-bg);color:var(--cp-text);}
:root[data-theme] .gx-app-layout,
:root[data-theme] .gx-app-layout > .ant-layout,
:root[data-theme] .gx-layout-content,
:root[data-theme] .gx-main-content-wrapper{background:var(--cp-bg);}
:root[data-theme="light"] .gx-app-sidebar,
:root[data-theme="light"] .gx-layout-sider,
:root[data-theme="light"] .ant-layout-sider{background:var(--cp-side-bg)!important;border-right:1px solid var(--cp-border);}
:root[data-theme="light"] .gx-sidebar-content,
:root[data-theme="light"] .gx-sidebar-content *{color:var(--cp-text);}
:root[data-theme="light"] .gx-sidebar-content .ant-menu,
:root[data-theme="light"] .gx-sidebar-content .ant-menu-sub{background:transparent!important;}
:root[data-theme="light"] .gx-sidebar-content .ant-menu-item a,
:root[data-theme="light"] .gx-sidebar-content .ant-menu-item span{color:var(--cp-text)!important;}
:root[data-theme="light"] .gx-sidebar-content .ant-menu-item-selected,
:root[data-theme="light"] .gx-sidebar-content .ant-menu-item:hover{background:var(--cp-side-item-hover)!important;}
/* Sidebar başlığı (logo şeridi) ve alt boşluk açık temada koyu kalıyordu:
   "chatpilots.ai" beyaz metin açık zeminde okunmuyordu (canlı ölçüm). */
:root[data-theme="light"] .gx-layout-sider-header,
:root[data-theme="light"] .ant-layout-sider-children,
:root[data-theme="light"] .gx-sidebar-content{background:transparent!important;}
:root[data-theme="light"] .gx-layout-sider-header{border-bottom:1px solid var(--cp-border);}
:root[data-theme="light"] .gx-layout-sider-header .cp-logo-word{color:#0f172a!important;}
:root[data-theme="light"] .cp-logo:hover{background:rgba(15,23,42,.05);}
:root[data-theme="light"] .cp-panel{background:var(--cp-bg);color:var(--cp-text);}
/* Form alanları koyu temada yarı saydam siyahtı; açık temada gri kutu gibi
   duruyordu (canlı: Kişisel Asistan giriş satırı). */
:root[data-theme="light"] .cp-panel input,
:root[data-theme="light"] .cp-panel select,
:root[data-theme="light"] .cp-panel textarea{background:#fff!important;border-color:var(--cp-border)!important;color:var(--cp-text)!important;}
:root[data-theme="light"] .cp-panel input::placeholder,
:root[data-theme="light"] .cp-panel textarea::placeholder{color:var(--cp-faint)!important;}
:root[data-theme="light"] .cp-panel::before{opacity:.35;}
:root[data-theme="light"] input,
:root[data-theme="light"] textarea,
:root[data-theme="light"] select{color-scheme:light;}
:root[data-theme="dark"] input,
:root[data-theme="dark"] textarea,
:root[data-theme="dark"] select{color-scheme:dark;}
/* KOYU TEMADA PENCERE VARSAYILANI — antd Modal'in kendi zemini BEYAZ.
   Sayfalarin cogu kendi '.xxx-modal .ant-modal-content' kuralini yaziyor ama
   yazmayan 20+ pencere var; koyu temada beyaz zemine acik metin biniyor ve
   icerik okunmuyordu (canli bulgu: hesap menusu). Buradaki kural TEK
   siniflik, yani sayfaya ozel '.pm-modal .ant-modal-content' gibi kurallar
   her zaman bunu eziyor — mevcut tasarimlar bozulmuyor, yalniz hic kurali
   olmayanlar tema yuzeyine oturuyor. */
:root[data-theme="dark"] .ant-modal-content{background:var(--cp-elevated);color:var(--cp-text);}
:root[data-theme="dark"] .ant-modal-header{background:var(--cp-elevated);border-bottom:1px solid var(--cp-border);}
:root[data-theme="dark"] .ant-modal-title{color:var(--cp-title);}
:root[data-theme="dark"] .ant-modal-close{color:var(--cp-faint);}
:root[data-theme="dark"] .ant-modal-footer{background:var(--cp-elevated);border-top:1px solid var(--cp-border);}

/* Açık temada koyu modal iskeleti okunur kalsın */
:root[data-theme="light"] .cp-dark-modal .ant-modal-content{background:var(--cp-surface);color:var(--cp-text);}
:root[data-theme="light"] .cp-dark-modal .ant-modal-header{background:var(--cp-surface);border-bottom:1px solid var(--cp-border);}
:root[data-theme="light"] .cp-dark-modal .ant-modal-title,
:root[data-theme="light"] .cp-dark-modal .ant-modal-close{color:var(--cp-title)!important;}
:root[data-theme="light"] .ant-select-dropdown{background:var(--cp-surface)!important;border-color:#1e293b!important;}
:root[data-theme="light"] .ant-select-dropdown .ant-select-item{color:var(--cp-text);}

/* ═══════════ MOBİL DÜZELTMELERİ (≤767px) ═══════════
 * Ayarlar'daki 25 sayfanın tamamı canlıda 375px'te tek tek ölçüldü; taşmaların
 * hepsi ÜÇ tekrar eden desenden geliyordu. Sayfa sayfa yamamak yerine deseni
 * burada bir kez kapatıyoruz — yeni sayfa eklendiğinde de korunur.
 *
 * 1) NEGATİF MARGIN TAŞMASI. Sayfa kabukları masaüstünde tam genişlik için
 *    ebeveynin dolgusunu "margin:-32px" + "width:calc(100% + 64px)" ile iptal
 *    ediyor. Mobilde ebeveynin dolgusu 12px'e düşüyor ama kabuklar hâlâ -32px
 *    uyguluyordu → her yanda 20px taşma (ölçüm: 372px alanda 412px kabuk).
 *    "[class][class]" ile özgüllük bilerek yükseltildi: sayfaların kendi
 *    kuralları "!important" taşıyor, eşit özgüllükte onlar kazanıyordu.
 *
 * 2) SEKME ÇUBUĞU. antd v3'ün tab bar'ı dar alana sığmayınca taşan sekmeler
 *    tıklanamaz oluyordu (Sadakat 763px, Email/SMS Marketing 629px). Artık
 *    yatay kayıyor; ok düğmeleri dar ekranda yer kapladığı için gizlendi.
 *
 * 3) GENİŞ TABLO. Bazı tablolar kaydırma kabı olmadan 837px'e kadar çıkıyordu
 *    (Agent Performans). Tablo kendisi kaydırma kabına dönüşüyor. ".aff-tbl"
 *    hariç: Danışmanlık paneli tabloyu zaten karta çeviriyor.
 *    Boş liste rozeti de tablonun tam genişliğine yayılıp ekran dışında
 *    kalıyordu; sticky ile görünür tutuluyor.
 */
@media (max-width: 767px){
  .gx-main-content-wrapper[class][class]{max-width:100vw!important;margin-left:0!important;margin-right:0!important;}
  .gx-main-content-wrapper > [class][class]{margin-left:0!important;margin-right:0!important;max-width:100%!important;}
  .gx-main-content-wrapper > *{margin-left:0!important;margin-right:0!important;max-width:100%!important;}
  [class*="-shell"].gx-main-content-wrapper{margin-left:0!important;margin-right:0!important;width:100%!important;max-width:100%!important;}
  .gx-main-content-wrapper > [class*="-shell"]{width:100%!important;}

  .ant-tabs-nav-container,.ant-tabs-nav-wrap{overflow-x:auto!important;-webkit-overflow-scrolling:touch;}
  .ant-tabs-nav-container{height:auto!important;}
  .ant-tabs-nav-scroll{overflow:visible!important;}
  .ant-tabs-tab-prev,.ant-tabs-tab-next{display:none!important;}

  .ant-table-body,.ant-table-content{overflow-x:auto!important;-webkit-overflow-scrolling:touch;}
  .gx-main-content-wrapper table:not(.aff-tbl){display:block;overflow-x:auto;-webkit-overflow-scrolling:touch;max-width:100%;}
  .gx-main-content-wrapper .ant-table table:not(.aff-tbl){display:table;overflow:visible;}

  .ant-table-placeholder{padding:16px 8px!important;text-align:left!important;}
  .ant-table-placeholder > div{max-width:calc(100vw - 48px)!important;margin:0!important;}
  .ant-table-placeholder .ant-empty{position:sticky;left:8px;max-width:calc(100vw - 48px);margin:0!important;}
  .ant-table-placeholder .ant-empty-image{max-width:100%;height:auto;}
}
`;

function ensureStyleTag() {
  let el = document.getElementById("cp-theme-vars");
  if (!el) {
    el = document.createElement("style");
    el.id = "cp-theme-vars";
    document.head.appendChild(el);
  }
  return el;
}

export function getTheme() {
  try {
    const t = localStorage.getItem(KEY);
    return THEMES.indexOf(t) >= 0 ? t : "dark";
  } catch (e) {
    return "dark";
  }
}

export function applyTheme(theme) {
  const t = THEMES.indexOf(theme) >= 0 ? theme : getTheme();
  const vars = PALETTE[t];
  const decls = Object.keys(vars).map((k) => `${k}:${vars[k]};`).join("");
  ensureStyleTag().textContent = `:root{${decls}}\n${GLOBAL_CSS}`;
  try {
    document.documentElement.setAttribute("data-theme", t);
    document.documentElement.style.colorScheme = t;
  } catch (e) { /* yoksay */ }
  return t;
}

export function setTheme(theme) {
  const t = applyTheme(theme);
  try { localStorage.setItem(KEY, t); } catch (e) { /* yoksay */ }
  try { window.dispatchEvent(new CustomEvent(EVENT, { detail: t })); } catch (e) { /* yoksay */ }
  return t;
}

export function toggleTheme() {
  return setTheme(getTheme() === "dark" ? "light" : "dark");
}

/** Tema değişince haber ver (React bileşenleri kendi sınıflarını günceller). */
export function onThemeChange(fn) {
  const h = (e) => fn((e && e.detail) || getTheme());
  window.addEventListener(EVENT, h);
  return () => window.removeEventListener(EVENT, h);
}

export default { THEMES, getTheme, setTheme, applyTheme, toggleTheme, onThemeChange };
